import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, of, timer } from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  exhaustMap,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs/operators';
import {
  AutoTagFetchResult,
  IAutoTagDecision,
  IAutoTagLink,
  IAutoTagRunRequest,
} from 'src/app/core/models/i-auto-tag-responses';
import { IAutoTagResult } from 'src/app/core/models/i-auto-tag';
import { IAutoTagContent } from 'src/app/shared/utils/auto-tag-content-hash';
import { AutoTagFixtureFlag, AutoTaggingService } from './auto-tagging.service';

export const AUTO_TAG_POLL_INTERVAL_MS = 2000;
export const AUTO_TAG_POLL_DEADLINE_MS = 60000;
// One failed poll is not the run failing - the next tick usually answers. Only this many
// consecutive failures means the feature is genuinely unreachable.
export const AUTO_TAG_POLL_MAX_CONSECUTIVE_FAILURES = 3;

// Shared hint copy so the panel button and any status bar never explain a refusal differently.
export const AUTO_TAG_UNAVAILABLE_MESSAGE = 'Auto Tag unavailable — try again';
export const AUTO_TAG_RUN_FAILED_MESSAGE =
  'This Auto Tag failed — run Auto Tag again';
export const AUTO_TAG_READ_ONLY_HINT = 'Switch to edit mode to auto tag';
export const AUTO_TAG_SAVE_FIRST_HINT = 'Save this content to auto tag';
export const AUTO_TAG_ALREADY_RUNNING_HINT = 'Auto tagging is already running';
export const AUTO_TAG_SAVE_IN_PROGRESS_HINT = 'Wait for the save to finish';
export const AUTO_TAG_READY_HINT = 'Suggest topics, tags and a surtitle';
// Shown in place of the progress box when Auto Tag is pressed but the content is byte-identical to
// the last completed run (the backend would only replay it), then auto-dismissed.
export const AUTO_TAG_NO_CHANGE_MESSAGE =
  'Nothing has changed since the last Auto Tag — the current suggestions are up to date.';
export const AUTO_TAG_NO_CHANGE_DISPLAY_MS = 10000;

export type AutoTagRunStatus =
  | 'idle'
  | 'saving'
  | 'triggering'
  | 'running'
  | 'complete'
  | 'error';
export type AutoTagRunErrorReason = 'unavailable' | 'run_failed';

// The article-owned capabilities a run needs, supplied by the parent article form so the panel
// stays the state service's only client. A run itself sends only { trigger } (cms-api reads the
// just-saved content server-side); getContent/contentChanges$ exist only so the panel can hash the
// live edit buffer and warn when it has diverged from a completed run's content hash.
export interface AutoTagArticleGateway {
  isDirty(): boolean;
  isSaving(): boolean;
  // Emits once on success; completes without emitting when a save dialog is cancelled.
  saveExisting$(): Observable<unknown>;
  getContent(): IAutoTagContent;
  contentChanges$(): Observable<unknown>;
}

const SAVE_CANCELLED = Symbol('auto-tag-save-cancelled');
const POLL_TICK_FAILED = Symbol('auto-tag-poll-tick-failed');

export interface IAutoTagRunState {
  articleId: string | null;
  status: AutoTagRunStatus;
  runId: string | null;
  errorReason: AutoTagRunErrorReason | null;
  result: IAutoTagResult | null;
  // The content hash the run was computed against (from the poll). Lets the panel tell when the
  // article has been edited since it was tagged. Null until a run completes.
  contentHash: string | null;
  // True when this state was rehydrated on open (a run started earlier or by another user) rather
  // than produced by a run this session. The panel marks a restored run's tags with the AI icon
  // instead of re-applying them and re-reporting acceptance.
  restored: boolean;
  // The AI-linked taxonomy items from the shared links table (independent of any run). The durable
  // source for the "added by AI" icon: it survives reload, content edits and run expiry.
  linked: IAutoTagLink[];
}

const INITIAL_STATE: IAutoTagRunState = {
  articleId: null,
  status: 'idle',
  runId: null,
  errorReason: null,
  result: null,
  contentHash: null,
  restored: false,
  linked: [],
};

@Injectable({
  providedIn: 'root',
})
export class AutoTaggingStateService {
  private readonly stateSubject = new BehaviorSubject<IAutoTagRunState>(
    INITIAL_STATE,
  );
  public readonly state$ = this.stateSubject.asObservable();

  private pollSub: Subscription | null = null;
  private restoreSub: Subscription | null = null;
  private linksSub: Subscription | null = null;
  private isActive = false;
  // Bumped on every activate/deactivate so callbacks for a previous article recognise they are
  // stale and never write into the current article's state.
  private activationEpoch = 0;

  constructor(private autoTaggingService: AutoTaggingService) {}

  activate(articleId: string): void {
    if (this.isActive && this.stateSubject.value.articleId === articleId) {
      return;
    }
    this.isActive = true;
    const epoch = ++this.activationEpoch;
    this.stopPolling();
    this.restoreSub?.unsubscribe();
    this.linksSub?.unsubscribe();
    this.stateSubject.next({ ...INITIAL_STATE, articleId });

    // The AI-linked items are the durable source for the icon: read them up front (independent of
    // any run) so a reload, content edit or expired run still shows which tags the AI added.
    this.linksSub = this.autoTaggingService.getLinks$(articleId).subscribe((linked) => {
      if (epoch !== this.activationEpoch) {
        return;
      }
      this.patchState({ linked });
    });

    // Opening an article never *starts* a run - only the Auto Tag button does (see run()). But it
    // does restore one already in progress or completed, so a reload, or another user opening the
    // same article, sees that run (and cannot start a second) instead of a fresh idle panel. A
    // restored 'running' run is polled under the normal deadline, so a run wedged past its lease
    // releases to 'error' rather than showing busy forever.
    this.restoreSub = this.autoTaggingService.getAutoTags$(articleId).subscribe({
      next: (result) => {
        if (epoch !== this.activationEpoch) {
          return;
        }
        if (result.kind === 'running') {
          this.patchState({
            status: 'running',
            runId: result.runId,
            restored: true,
          });
          this.startPolling(articleId);
        } else if (result.kind === 'complete') {
          this.patchState({
            status: 'complete',
            runId: result.runId,
            result: result.result,
            contentHash: result.contentHash,
            restored: true,
          });
        }
        // no_run and failed stay idle: the never-ran path, and a run that failed before the editor
        // arrived, which is theirs to re-run rather than an error to greet them with.
      },
      error: () => {
        if (epoch !== this.activationEpoch) {
          return;
        }
        // Restore failures degrade to idle: a page load never greets the user with an error for a
        // feature they have not touched yet.
        this.patchState({ status: 'idle' });
      },
    });
  }

  activateUnsaved(): void {
    this.isActive = true;
    this.cancelRequests();
    this.stateSubject.next(INITIAL_STATE);
  }

  deactivate(): void {
    this.isActive = false;
    this.cancelRequests();
  }

  private cancelRequests(): void {
    this.activationEpoch++;
    this.restoreSub?.unsubscribe();
    this.restoreSub = null;
    this.linksSub?.unsubscribe();
    this.linksSub = null;
    this.stopPolling();
  }

  run(
    gateway: AutoTagArticleGateway,
    trigger: IAutoTagRunRequest['trigger'] = 'publish',
    fixture?: AutoTagFixtureFlag,
  ): void {
    const state = this.stateSubject.value;
    if (
      !state.articleId ||
      state.status === 'saving' ||
      state.status === 'triggering' ||
      state.status === 'running' ||
      gateway.isSaving()
    ) {
      return;
    }
    const articleId = state.articleId;
    const epoch = this.activationEpoch;

    if (!gateway.isDirty()) {
      this.postRun(articleId, trigger, fixture, epoch);
      return;
    }

    this.patchState({ status: 'saving' });
    // take(1)+defaultIfEmpty tells a successful save (emits) from a cancelled dialog (empty).
    gateway
      .saveExisting$()
      .pipe(take(1), defaultIfEmpty(SAVE_CANCELLED))
      .subscribe({
        next: (saveResult) => {
          if (epoch !== this.activationEpoch) {
            return;
          }
          if (saveResult === SAVE_CANCELLED) {
            this.patchState({ status: 'idle' });
            return;
          }
          this.postRun(articleId, trigger, fixture, epoch);
        },
        error: () => {
          if (epoch !== this.activationEpoch) {
            return;
          }
          // Save failed: the CMS surfaces its own save-error UI - the panel returns to idle.
          this.patchState({ status: 'idle' });
        },
      });
  }

  // Report accept/remove feedback for the current run to cms-api (best-effort). No-op if there is
  // no active run to attribute the decisions to.
  reportDecisions(decisions: IAutoTagDecision[]): void {
    const state = this.stateSubject.value;
    if (!decisions.length || !state.articleId || !state.runId) {
      return;
    }
    this.autoTaggingService
      .submitDecisions$(state.articleId, state.runId, decisions)
      .subscribe();
  }

  private postRun(
    articleId: string,
    trigger: IAutoTagRunRequest['trigger'],
    fixture: AutoTagFixtureFlag | undefined,
    epoch: number,
  ): void {
    this.patchState({
      status: 'triggering',
      result: null,
      restored: false,
      errorReason: null,
    });
    const body: IAutoTagRunRequest = { trigger };

    this.autoTaggingService
      .triggerRun$(articleId, body, fixture ? { fixture } : undefined)
      .subscribe({
        next: (result) => {
          if (epoch !== this.activationEpoch) {
            return;
          }
          // A 409 run_in_flight is absorbed by the service: either way a run exists, so the poller
          // takes over.
          this.patchState({ status: 'running', runId: result.runId });
          this.startPolling(articleId);
        },
        error: () => {
          if (epoch !== this.activationEpoch) {
            return;
          }
          this.patchState({ status: 'error', errorReason: 'unavailable' });
        },
      });
  }

  private startPolling(articleId: string): void {
    if (this.pollSub && !this.pollSub.closed) {
      return;
    }
    let consecutiveFailures = 0;
    this.pollSub = timer(0, AUTO_TAG_POLL_INTERVAL_MS)
      .pipe(
        // Overall deadline: rxjs timeout() would never fire because every poll emits.
        takeUntil(timer(AUTO_TAG_POLL_DEADLINE_MS)),
        // exhaustMap: a GET slower than the interval skips ticks instead of being cancelled. The
        // catchError is inside it so a failed tick ends that request, not the timer.
        exhaustMap(() =>
          this.autoTaggingService
            .getAutoTags$(articleId)
            .pipe(catchError(() => of<AutoTagFetchResult | symbol>(POLL_TICK_FAILED))),
        ),
        takeWhile(
          (result) =>
            result === POLL_TICK_FAILED ||
            (result as AutoTagFetchResult).kind === 'running',
          true,
        ),
      )
      .subscribe({
        next: (result) => {
          if (result === POLL_TICK_FAILED) {
            consecutiveFailures++;
            if (consecutiveFailures >= AUTO_TAG_POLL_MAX_CONSECUTIVE_FAILURES) {
              this.patchState({ status: 'error', errorReason: 'unavailable' });
              this.stopPolling();
            }
            return;
          }
          consecutiveFailures = 0;
          const fetch = result as AutoTagFetchResult;
          if (fetch.kind === 'complete') {
            this.patchState({
              status: 'complete',
              runId: fetch.runId,
              result: fetch.result,
              contentHash: fetch.contentHash,
              restored: false,
            });
            return;
          }
          if (fetch.kind === 'failed') {
            this.patchState({
              status: 'error',
              runId: fetch.runId,
              errorReason: 'run_failed',
            });
            return;
          }
          if (fetch.kind === 'no_run') {
            // The run vanished mid-poll (mock store reset) - degrade quietly to idle.
            this.patchState({ status: 'idle' });
          }
        },
        error: () =>
          this.patchState({ status: 'error', errorReason: 'unavailable' }),
        complete: () => {
          // External stops unsubscribe rather than complete, so reaching here with the run still in
          // progress means the deadline elapsed.
          if (this.stateSubject.value.status === 'running') {
            this.patchState({ status: 'error', errorReason: 'unavailable' });
          }
        },
      });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private patchState(patch: Partial<IAutoTagRunState>): void {
    this.stateSubject.next({ ...this.stateSubject.value, ...patch });
  }
}
